import React, { useState, useEffect, Fragment, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import {
  PlusIcon,
  XMarkIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { notificationAPI } from '../../services/api';
import CodeMirror from '@uiw/react-codemirror';
import { html } from '@codemirror/lang-html';
import { NOTIFICATION_EVENT_TYPES } from '../../constants/notificationEventTypes';
import { sanitizeHtml } from '../../utils/sanitize';
import TableColumnFilter from '../../components/TableColumnFilter';
import ActiveFilterChips, { FilterChip } from '../../components/ActiveFilterChips';

interface EmailTemplate {
  id: string;
  name: string;
  description?: string;
  eventType: string;
  entityType: string;
  subject: string;
  htmlBody: string;
  textBody: string;
  isActive: boolean;
  variables?: any;
  createdAt: string;
  updatedAt: string;
}

// Use shared event types to keep in sync with Notification Rules
const EVENT_TYPES = NOTIFICATION_EVENT_TYPES;

// Entity types for filtering
const ENTITY_TYPES = [
  { value: 'CASE', label: 'Case' },
  { value: 'INCIDENT', label: 'Incident' },
  { value: 'PROBLEM', label: 'Problem' },
  { value: 'CHANGE', label: 'Change' },
  { value: 'SERVICE_REQUEST', label: 'Service Request' },
  { value: 'ALERT', label: 'Alert' },
  { value: 'KNOWLEDGE', label: 'Knowledge' },
  { value: 'TIME_ENTRY', label: 'Time Entry' },
];

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const ACTIVE_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const EmailTemplatesManagement: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const patchSearchParams = useCallback(
    (updates: Record<string, string | null | undefined>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, val] of Object.entries(updates)) {
            if (val === null || val === undefined || val === '') {
              next.delete(key);
            } else {
              next.set(key, val);
            }
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const filterEventType = searchParams.get('eventType') ?? '';
  const filterEntityType = searchParams.get('entityType') ?? '';
  const filterActive = searchParams.get('active') ?? 'all';
  const filterName = searchParams.get('name') ?? '';
  const filterSubject = searchParams.get('subject') ?? '';

  const page = useMemo(() => {
    const p = parseInt(searchParams.get('page') || '1', 10);
    return Number.isFinite(p) && p >= 1 ? p : 1;
  }, [searchParams]);

  const pageSize = useMemo(() => {
    const ps = parseInt(searchParams.get('pageSize') || '10', 10);
    return PAGE_SIZE_OPTIONS.includes(ps) ? ps : 10;
  }, [searchParams]);

  const [allTemplates, setAllTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Create/Edit Modal
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    eventType: '',
    entityType: '',
    subject: '',
    htmlBody: '',
    textBody: '',
    isActive: true,
  });
  const [saving, setSaving] = useState(false);

  // Preview Modal
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<EmailTemplate | null>(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  // Delete Confirmation Modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingTemplate, setDeletingTemplate] = useState<EmailTemplate | null>(null);

  useEffect(() => {
    if (filterEntityType && filterEventType) {
      const eventType = EVENT_TYPES.find((et) => et.value === filterEventType);
      if (eventType && eventType.entityType !== filterEntityType) {
        patchSearchParams({ eventType: null, page: '1' });
      }
    }
  }, [filterEntityType, filterEventType, patchSearchParams]);

  useEffect(() => {
    fetchTemplates();
  }, [filterEventType, filterEntityType, filterActive]);

  const eventTypeFilterOptions = useMemo(() => {
    let filtered = EVENT_TYPES;
    if (filterEntityType) {
      filtered = filtered.filter((et) => et.entityType === filterEntityType);
    }
    const seen = new Map<string, string>();
    for (const et of filtered) {
      if (!seen.has(et.value)) seen.set(et.value, et.label);
    }
    return [...seen.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }, [filterEntityType]);

  const entityTypeFilterOptions = useMemo(
    () => ENTITY_TYPES.map((et) => ({ value: et.value, label: et.label })),
    []
  );

  const filteredTemplates = useMemo(() => {
    let result = allTemplates;
    if (filterName.trim()) {
      const q = filterName.trim().toLowerCase();
      result = result.filter(
        (t) =>
          t.name.toLowerCase().includes(q) ||
          (t.description && t.description.toLowerCase().includes(q))
      );
    }
    if (filterSubject.trim()) {
      const q = filterSubject.trim().toLowerCase();
      result = result.filter((t) => t.subject.toLowerCase().includes(q));
    }
    return result;
  }, [allTemplates, filterName, filterSubject]);

  const totalCount = filteredTemplates.length;
  const templates = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    return filteredTemplates.slice(startIndex, startIndex + pageSize);
  }, [filteredTemplates, page, pageSize]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (filterEventType) params.eventType = filterEventType;
      if (filterEntityType) params.entityType = filterEntityType;
      if (filterActive !== 'all') params.isActive = filterActive === 'active';

      const response = await notificationAPI.templates.list(params);
      setAllTemplates(response.data.data || response.data || []);
    } catch (error: any) {
      showError(error.response?.data?.message || 'Failed to fetch templates');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Client-side pagination values
  const totalPages = Math.ceil(totalCount / pageSize) || 1;
  const startEntry = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const endEntry = Math.min(page * pageSize, totalCount);

  const handleCreateNew = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      description: '',
      eventType: '',
      entityType: '',
      subject: '',
      htmlBody: '',
      textBody: '',
      isActive: true,
    });
    setShowFormModal(true);
  };

  const handleEdit = (template: EmailTemplate) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      eventType: template.eventType,
      entityType: template.entityType,
      subject: template.subject,
      htmlBody: template.htmlBody,
      textBody: template.textBody,
      isActive: template.isActive,
    });
    setShowFormModal(true);
  };

  const handleSave = async () => {
    // Basic validation for required fields
    if (!formData.name || !formData.eventType || !formData.subject || !formData.htmlBody) {
      showError('Please fill in all required fields (Name, Event Type, Subject, and HTML Body).');
      return;
    }

    try {
      setSaving(true);
      if (editingTemplate) {
        await notificationAPI.templates.update(editingTemplate.id, formData);
        showSuccess('Template updated successfully');
      } else {
        await notificationAPI.templates.create(formData);
        showSuccess('Template created successfully');
      }
      setShowFormModal(false);
      fetchTemplates();
    } catch (error: any) {
      showError(error.response?.data?.message || 'Failed to save template');
      console.error('Error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingTemplate) return;

    try {
      await notificationAPI.templates.delete(deletingTemplate.id);
      showSuccess('Template deleted successfully');
      setShowDeleteModal(false);
      setDeletingTemplate(null);
      fetchTemplates();
    } catch (error: any) {
      showError(error.response?.data?.message || 'Failed to delete template');
      console.error('Error:', error);
    }
  };

  const handlePreview = async (template: EmailTemplate) => {
    setPreviewTemplate(template);
    setShowPreviewModal(true);
    setPreviewLoading(true);

    try {
      // Sample data for preview - dynamic based on event type
      const baseEntityData = {
        number: template.eventType.includes('INCIDENT') ? 'INC-12345' :
                template.eventType.includes('PROBLEM') ? 'PRB-12345' :
                template.eventType.includes('CHANGE') ? 'CHG-12345' :
                template.eventType.includes('SERVICE_REQUEST') ? 'REQ-12345' :
                'CASE-12345',
        shortDescription: template.eventType.includes('CHANGE')
          ? 'Sphera (SpheraCloud) Patching On Dev/Test Servers on 11th June 2020'
          : 'Sample issue: Database server connection timeout',
        description: 'Detailed description of the issue',
        priority: 'HIGH',
        state: template.eventType.includes('RESOLVED') ? 'resolved' :
               template.eventType.includes('CLOSED') ? 'closed' :
               template.eventType.includes('ON_HOLD') ? 'on_hold' :
               template.eventType.includes('AWAITING_INFO') ? 'awaiting_info' :
               template.eventType.includes('SOLUTION_PROPOSED') ? 'solution_proposed' : 'open',
        category: template.eventType.includes('CHANGE') ? 'Patching' : 'Technical Support',
        solution: 'Restarted database service and cleared connection pool',
        resolutionCode: 'solved_permanently',
        resolutionNotes: 'Root cause was identified as memory leak in connection pool. Applied patch and restarted service.',
        closureNotes: 'Verified resolution with customer. No further action required. Closing incident.',
        // Change-specific fields
        affectedCIs: 'DEV-SERVER-01, TEST-SERVER-02',
        comments: 'Patching scheduled during maintenance window',
        scheduledStartDate: '2020-06-11T20:00:00Z',
        scheduledEndDate: '2020-06-11T23:00:00Z',
        rejectionReason: 'Insufficient testing documentation provided',
        // Change Closed specific fields
        configurationItem: 'SpheraCloud Production Environment',
        risk: 'Moderate',
        changeType: 'Standard',
        parent: '',
        impact: '3 - Low',
        closeCode: 'Successful',
        closeNotes: 'All changes implemented successfully. No issues reported.',
        createdAt: '2022-03-04T16:40:23Z',
        cabDate: '',
        implementationPlan: 'Apply security patches to Dev and Test servers during maintenance window',
        justification: 'Critical security patches required to address vulnerabilities',
        riskImpactAnalysis: 'Low impact - only Dev/Test servers affected. Minimal risk with proper rollback plan.',
        backoutPlan: 'Restore from pre-patching snapshots if issues occur',
        testPlan: 'Verify server functionality and application compatibility post-patching',
        commands: 'yum update -y; systemctl restart httpd',
      };

      // Enhanced problem data with RCA fields for Problem templates
      const problemData = template.eventType.includes('PROBLEM') ? {
        ...baseEntityData,
        problemStateLabel: 'Open',
        project: 'E-Commerce Platform Migration',
        customer: 'Acme Corporation',
        incidentNumber: 'INC0009876',
        severity: 'HIGH',
        reportedIssue: 'Application server experiencing severe performance degradation during peak hours, resulting in slow response times and intermittent timeouts for end users.',
        problemDate: '2025-11-10T14:30:00Z',
        actualServiceImpact: 'Approximately 2,500 users experienced degraded performance for 4 hours. Transaction processing times increased from 2 seconds to 45 seconds. No data loss occurred.',
        briefAnalysis: 'Initial investigation revealed high CPU utilization (95%+) and memory exhaustion on the application server. Database connection pool was exhausted with 500+ active connections, exceeding the configured limit of 200 connections.',
        detailedAnalysis: `Timeline of Investigation:

1. 14:30 - First alerts received indicating high response times
2. 14:45 - Application server monitoring shows CPU at 98%, memory at 92%
3. 15:00 - Database team confirms connection pool exhaustion (512 active connections)
4. 15:15 - Code review identified inefficient database query in recent deployment
5. 15:30 - Analysis of query execution plans revealed missing index causing full table scans
6. 16:00 - Temporary mitigation applied by restarting application server and increasing connection pool
7. 16:30 - Performance returned to normal levels

Technical Findings:
- Recent code deployment (v2.3.5) introduced a new reporting feature
- The reporting query was not optimized and lacked proper indexing
- Query was executed once per user session, creating excessive database load
- Missing index on 'transaction_date' column in 'orders' table (15M rows)
- Connection pooling configuration was not adjusted for increased load
- No load testing was performed before deployment

Infrastructure Analysis:
- Application server: 8 vCPU, 16GB RAM (adequate for normal load)
- Database server: 16 vCPU, 32GB RAM with SSD storage
- Network latency between app and DB server: 2-3ms (acceptable)
- No infrastructure issues identified`,
        rootCause: 'The root cause was an unoptimized database query introduced in release v2.3.5 that was missing a critical index. This query performed full table scans on a 15 million row table, executed once per user session during peak hours. Combined with insufficient connection pool sizing and lack of pre-deployment load testing, this resulted in connection pool exhaustion and severe performance degradation affecting 2,500 concurrent users.',
        correctiveActions: `Immediate Actions Taken:
1. Created composite index on 'orders' table (transaction_date, customer_id, status) - completed 2025-11-10 16:45
2. Optimized the reporting query to use indexed columns and reduce data retrieval - completed 2025-11-10 17:30
3. Increased database connection pool from 200 to 400 connections - completed 2025-11-10 16:15
4. Deployed hotfix v2.3.6 with query optimization - completed 2025-11-10 18:00
5. Implemented query result caching for the reporting feature (15-minute TTL) - completed 2025-11-11 10:00

Performance Improvements:
- Query execution time reduced from 45 seconds to 0.8 seconds
- Connection pool utilization reduced from 100% to 35% during peak hours
- CPU utilization decreased from 95% to 42% average
- Memory usage stabilized at 65% average`,
        chronologyOfEvents: `Nov 08, 2025 09:00 - Release v2.3.5 deployed to production with new reporting feature
Nov 08, 2025 09:30 - First users begin accessing the new reporting dashboard
Nov 10, 2025 14:00 - Peak user activity begins (lunch hour in primary timezone)
Nov 10, 2025 14:30 - First performance degradation alerts received from monitoring system
Nov 10, 2025 14:35 - On-call engineer acknowledges alerts and begins initial investigation
Nov 10, 2025 14:45 - Application server metrics show CPU at 98%, memory at 92%
Nov 10, 2025 14:50 - Incident escalated to Database team for connection pool analysis
Nov 10, 2025 15:00 - Database team confirms connection pool exhaustion (512/200 connections)
Nov 10, 2025 15:15 - Code review initiated to identify changes in recent deployment
Nov 10, 2025 15:20 - Problematic reporting query identified in v2.3.5 release
Nov 10, 2025 15:30 - Query execution plan analysis reveals missing index on orders table
Nov 10, 2025 15:45 - Emergency change request approved for connection pool increase
Nov 10, 2025 16:00 - Connection pool increased to 400, application server restarted
Nov 10, 2025 16:15 - Temporary performance improvement observed
Nov 10, 2025 16:30 - Service performance returns to acceptable levels
Nov 10, 2025 17:00 - Development team begins work on query optimization
Nov 10, 2025 17:30 - Optimized query developed and tested in staging environment
Nov 10, 2025 18:00 - Hotfix v2.3.6 deployed with optimized query and new index
Nov 10, 2025 18:15 - Performance monitoring confirms issue resolution
Nov 10, 2025 18:30 - Incident declared resolved, monitoring continues
Nov 11, 2025 10:00 - Query result caching implemented for additional performance improvement
Nov 12, 2025 14:00 - RCA session conducted with stakeholders
Nov 15, 2025 09:00 - RCA document presented to review committee
Nov 16, 2025 10:00 - Preventive actions approved and assigned to owners`,
        rcaRecommendationStatus: 'Approved',
        closedBy: 'Jane Smith',
        reviewMatrix: [
          {
            presentedBy: 'Robert Johnson',
            reviewedBy: 'Sarah Williams',
            reviewDate: '2025-11-15',
            status: 'Approved'
          },
          {
            presentedBy: 'Robert Johnson',
            reviewedBy: 'Michael Brown',
            reviewDate: '2025-11-16',
            status: 'Approved'
          }
        ],
        problemTasks: [
          {
            description: 'Implement mandatory database query performance review in code review checklist',
            assignedToName: 'David Martinez (Database Team Lead)',
            dueDate: '2025-11-30T00:00:00Z',
            state: 'in_progress',
            serviceImprovements: 'Prevents unoptimized queries from reaching production'
          },
          {
            description: 'Establish load testing requirement for all releases affecting database operations',
            assignedToName: 'Emily Chen (QA Manager)',
            dueDate: '2025-12-15T00:00:00Z',
            state: 'new',
            serviceImprovements: 'Identifies performance issues before production deployment'
          },
          {
            description: 'Configure automated alerts for database connection pool utilization >70%',
            assignedToName: 'Kevin Thompson (DevOps Engineer)',
            dueDate: '2025-11-25T00:00:00Z',
            state: 'completed',
            serviceImprovements: 'Early warning system for resource exhaustion'
          },
          {
            description: 'Implement query execution time monitoring and automatic slow query logging',
            assignedToName: 'Lisa Anderson (Database Administrator)',
            dueDate: '2025-12-10T00:00:00Z',
            state: 'in_progress',
            serviceImprovements: 'Proactive identification of performance bottlenecks'
          },
          {
            description: 'Document and communicate database indexing best practices to development team',
            assignedToName: 'David Martinez (Database Team Lead)',
            dueDate: '2025-12-05T00:00:00Z',
            state: 'in_progress',
            serviceImprovements: 'Improves team knowledge and reduces similar incidents'
          }
        ]
      } : baseEntityData;

      const sampleData = {
        case: baseEntityData,
        incident: baseEntityData,
        problem: problemData,
        change: baseEntityData,
        serviceRequest: baseEntityData,
        caller: {
          firstName: 'John',
          lastName: 'Doe',
          email: 'john.doe@example.com',
        },
        assignedTo: {
          firstName: 'Jane',
          lastName: 'Smith',
          email: 'jane.smith@example.com',
        },
        previousAssignee: 'Bob Williams',
        previousAssignmentGroup: 'Level 1 Support Team',
        resolvedBy: {
          firstName: 'Alice',
          lastName: 'Johnson',
          email: 'alice.johnson@example.com',
        },
        closedBy: {
          firstName: 'Bob',
          lastName: 'Wilson',
          email: 'bob.wilson@example.com',
        },
        requestedBy: {
          firstName: 'Nitin',
          lastName: 'Kumar',
          email: 'nitin.kumar@example.com',
        },
        approvedBy: {
          firstName: 'Sarah',
          lastName: 'Johnson',
          email: 'sarah.johnson@example.com',
        },
        rejectedBy: {
          firstName: 'Michael',
          lastName: 'Brown',
          email: 'michael.brown@example.com',
        },
        assignmentGroup: {
          name: 'IT Support Team',
          email: 'support@example.com',
        },
        customer: {
          accountName: template.eventType.includes('CHANGE') ? 'Sphera Solutions' : 'Acme Corporation',
          emailAddress: 'contact@acme.com',
        },
        relatedCase: {
          number: 'CASE-98765',
          shortDescription: 'Related case for change request',
        },
        workNote: {
          note: 'Sample work note added to the ticket',
          isPublic: true,
        },
        metadata: {
          onHoldReason: 'Waiting for third-party vendor response',
          infoRequired: 'Please provide server access credentials and error logs',
          solution: 'Restarted database service and cleared connection pool',
        },
      };

      const response = await notificationAPI.templates.preview(template.id, sampleData);
      setPreviewHtml(response.data.data.htmlBody);
    } catch (error: any) {
      showError('Failed to generate preview');
      console.error('Error:', error);
    } finally {
      setPreviewLoading(false);
    }
  };

  const showSuccess = (message: string) => {
    setNotification({ message, type: 'success' });
    setTimeout(() => setNotification(null), 5000);
  };

  const showError = (message: string) => {
    setNotification({ message, type: 'error' });
    setTimeout(() => setNotification(null), 5000);
  };

  const renderColumnHeader = useCallback(
    (columnKey: string, columnLabel: string) => {
      switch (columnKey) {
        case 'name':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterName}
              placeholder="Filter by name..."
              onChange={(v) => {
                patchSearchParams({ name: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'eventType':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="searchable-select"
              options={eventTypeFilterOptions}
              value={filterEventType}
              placeholder="All Events"
              onChange={(v) => {
                patchSearchParams({ eventType: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'entity':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="single-select"
              options={entityTypeFilterOptions}
              value={filterEntityType}
              placeholder="All Entities"
              onChange={(v) => {
                patchSearchParams({
                  entityType: typeof v === 'string' && v ? v : null,
                  eventType: null,
                  page: '1',
                });
              }}
            />
          );
        case 'subject':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterSubject}
              placeholder="Filter by subject..."
              onChange={(v) => {
                patchSearchParams({ subject: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'status':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="single-select"
              options={ACTIVE_FILTER_OPTIONS}
              value={filterActive}
              placeholder="All Statuses"
              active={filterActive !== 'all'}
              onChange={(v) => {
                const val = typeof v === 'string' ? v : 'all';
                patchSearchParams({ active: val === 'all' ? null : val, page: '1' });
              }}
            />
          );
        default:
          return columnLabel;
      }
    },
    [
      filterName,
      filterEventType,
      filterEntityType,
      filterSubject,
      filterActive,
      eventTypeFilterOptions,
      entityTypeFilterOptions,
      patchSearchParams,
    ]
  );

  const activeFilterChips: FilterChip[] = useMemo(() => {
    const chips: FilterChip[] = [];
    if (filterName) {
      chips.push({ id: 'name', label: 'Name', value: filterName, color: 'blue' });
    }
    if (filterEntityType) {
      const label = ENTITY_TYPES.find((e) => e.value === filterEntityType)?.label || filterEntityType;
      chips.push({ id: 'entityType', label: 'Entity', value: label, color: 'teal' });
    }
    if (filterEventType) {
      const label = EVENT_TYPES.find((e) => e.value === filterEventType)?.label || filterEventType;
      chips.push({ id: 'eventType', label: 'Event', value: label, color: 'cyan' });
    }
    if (filterSubject) {
      chips.push({ id: 'subject', label: 'Subject', value: filterSubject, color: 'blue' });
    }
    if (filterActive !== 'all') {
      chips.push({
        id: 'active',
        label: 'Status',
        value: filterActive === 'active' ? 'Active' : 'Inactive',
        color: 'purple',
      });
    }
    return chips;
  }, [filterName, filterEntityType, filterEventType, filterSubject, filterActive]);

  const handleRemoveFilterChip = (chipId: string) => {
    switch (chipId) {
      case 'name':
        patchSearchParams({ name: null, page: '1' });
        break;
      case 'entityType':
        patchSearchParams({ entityType: null, eventType: null, page: '1' });
        break;
      case 'eventType':
        patchSearchParams({ eventType: null, page: '1' });
        break;
      case 'subject':
        patchSearchParams({ subject: null, page: '1' });
        break;
      case 'active':
        patchSearchParams({ active: null, page: '1' });
        break;
    }
  };

  const handleClearAllFilters = () => {
    patchSearchParams({
      name: null,
      entityType: null,
      eventType: null,
      subject: null,
      active: null,
      page: '1',
    });
  };

  // Calculate pagination values
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (page > 3) {
        pages.push('...');
      }

      const start = Math.max(2, page - 1);
      const end = Math.min(totalPages - 1, page + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (page < totalPages - 2) {
        pages.push('...');
      }

      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <div className="p-3 flex flex-col h-full min-h-0 overflow-hidden">
      <div className="bg-white shadow rounded-lg mb-2 flex-shrink-0">
        <div className="px-4 py-2">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Email Templates Management</h1>
              <p className="text-sm text-gray-600">
                Configure email templates for notification events across all ITSM modules.
              </p>
            </div>
            <button
              onClick={handleCreateNew}
              className="inline-flex items-center px-3 py-1.5 text-sm border border-transparent rounded-md font-medium text-white bg-primary-600 hover:bg-primary-700"
            >
              <PlusIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
              Create Template
            </button>
          </div>
        </div>
      </div>

      {/* Notification */}
      {notification && (
        <div className={`mb-2 p-3 rounded-md flex-shrink-0 ${
          notification.type === 'success'
            ? 'bg-green-50 border border-green-200'
            : 'bg-red-50 border border-red-200'
        }`}>
          <p className={`text-sm ${
            notification.type === 'success' ? 'text-green-800' : 'text-red-800'
          }`}>
            {notification.message}
          </p>
        </div>
      )}

      {activeFilterChips.length > 0 && (
        <div className="bg-white shadow rounded-lg mb-2 px-4 py-2 flex-shrink-0">
          <ActiveFilterChips
            chips={activeFilterChips}
            onRemove={handleRemoveFilterChip}
            onClearAll={handleClearAllFilters}
          />
        </div>
      )}

      {/* Templates Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden flex-1 min-h-0 flex flex-col">
        <div className="overflow-scroll flex-1" style={{ scrollbarGutter: 'stable' }}>
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-12">
            <EnvelopeIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No templates found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating a new email template.
            </p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  {renderColumnHeader('name', 'Name')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  {renderColumnHeader('eventType', 'Event Type')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  {renderColumnHeader('entity', 'Entity')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  {renderColumnHeader('subject', 'Subject')}
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  {renderColumnHeader('status', 'Status')}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {templates.map((template) => (
                <tr key={template.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-900 line-clamp-1">{template.name}</div>
                    {template.description && (
                      <div className="text-xs text-gray-500 line-clamp-1">{template.description}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-900 line-clamp-1">
                      {EVENT_TYPES.find(et => et.value === template.eventType)?.label || template.eventType}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 inline-flex text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                      {template.entityType}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-900 line-clamp-1" title={template.subject}>{template.subject}</div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {template.isActive ? (
                      <span className="px-2 py-0.5 inline-flex items-center text-xs font-medium rounded-full bg-green-100 text-green-800">
                        <CheckCircleIcon className="h-3.5 w-3.5 mr-1" />
                        Active
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 inline-flex items-center text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                        <XCircleIcon className="h-3.5 w-3.5 mr-1" />
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handlePreview(template)}
                        className="text-blue-600 hover:text-blue-900 p-1"
                        title="Preview"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(template)}
                        className="text-indigo-600 hover:text-indigo-900 p-1"
                        title="Edit"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          setDeletingTemplate(template);
                          setShowDeleteModal(true);
                        }}
                        className="text-red-600 hover:text-red-900 p-1"
                        title="Delete"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                  </tr>
                ))}
              </tbody>
            </table>
        )}
        </div>

        {/* Enhanced Pagination */}
        {!loading && templates.length > 0 && (
          <div className="bg-white border-t border-gray-200 px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex-1 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <label htmlFor="pageSize" className="text-sm text-gray-700">
                    Show
                  </label>
                  <select
                    id="pageSize"
                    value={pageSize}
                    onChange={(e) => {
                      patchSearchParams({ pageSize: e.target.value, page: '1' });
                    }}
                    className="border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                  >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                  <span className="text-sm text-gray-700">entries</span>
                </div>

                <div className="text-sm text-gray-700">
                  Showing {startEntry} to {endEntry} of {totalCount} entries
                </div>
              </div>

              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => patchSearchParams({ page: '1' })}
                    disabled={page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">First</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M15.79 14.77a.75.75 0 01-1.06.02l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 111.04 1.08L11.832 10l3.938 3.71a.75.75 0 01.02 1.06zm-6 0a.75.75 0 01-1.06.02l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 111.04 1.08L5.832 10l3.938 3.71a.75.75 0 01.02 1.06z" clipRule="evenodd" />
                    </svg>
                  </button>

                  <button
                    onClick={() => patchSearchParams({ page: String(Math.max(1, page - 1)) })}
                    disabled={page === 1}
                    className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Previous</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                    </svg>
                  </button>

                  {getPageNumbers().map((pageNum, index) => (
                    <React.Fragment key={index}>
                      {pageNum === '...' ? (
                        <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                          ...
                        </span>
                      ) : (
                        <button
                          onClick={() => patchSearchParams({ page: String(pageNum) })}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            page === pageNum
                              ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      )}
                    </React.Fragment>
                  ))}

                  <button
                    onClick={() => patchSearchParams({ page: String(Math.min(totalPages, page + 1)) })}
                    disabled={page === totalPages}
                    className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Next</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                    </svg>
                  </button>

                  <button
                    onClick={() => patchSearchParams({ page: String(totalPages) })}
                    disabled={page === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Last</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10.21 14.77a.75.75 0 01.02-1.06L14.168 10 10.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02zm-6 0a.75.75 0 01.02-1.06L8.168 10 4.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Transition appear show={showFormModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowFormModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-7xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex items-center justify-between mb-4">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      {editingTemplate ? 'Edit Email Template' : 'Create Email Template'}
                    </Dialog.Title>
                    <button
                      onClick={() => setShowFormModal(false)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="mt-4 space-y-4 max-h-[70vh] overflow-y-auto">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Template Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          placeholder="e.g., Case Created - Notify Assignee"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Event Type <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={formData.eventType}
                          onChange={(e) => {
                            const selected = EVENT_TYPES.find(et => et.value === e.target.value);
                            setFormData({
                              ...formData,
                              eventType: e.target.value,
                              entityType: selected?.entityType || ''
                            });
                          }}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        >
                          <option value="">Select event type</option>
                          {EVENT_TYPES.map(et => (
                            <option key={et.value} value={et.value}>{et.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Description</label>
                      <input
                        type="text"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder="Brief description of this template"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Subject <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        placeholder="e.g., New Case Assigned: {{case.number}}"
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Use Handlebars syntax for variables
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        HTML Body <span className="text-red-500">*</span>
                      </label>
                      <div className="border border-gray-300 rounded-md overflow-hidden">
                        <CodeMirror
                          value={formData.htmlBody}
                          height="400px"
                          extensions={[html()]}
                          onChange={(value) => setFormData({ ...formData, htmlBody: value })}
                          theme="light"
                          basicSetup={{
                            lineNumbers: true,
                            highlightActiveLineGutter: true,
                            highlightSpecialChars: true,
                            foldGutter: true,
                            drawSelection: true,
                            dropCursor: true,
                            allowMultipleSelections: true,
                            indentOnInput: true,
                            bracketMatching: true,
                            closeBrackets: true,
                            autocompletion: true,
                            rectangularSelection: true,
                            crosshairCursor: true,
                            highlightActiveLine: true,
                            highlightSelectionMatches: true,
                            closeBracketsKeymap: true,
                            searchKeymap: true,
                            foldKeymap: true,
                            completionKeymap: true,
                            lintKeymap: true,
                          }}
                        />
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        Use Handlebars syntax for variables: {`{{case.number}}, {{caller.firstName}}, etc.`}
                        <br />
                        <span className="text-amber-600 font-medium">Important:</span> Use triple braces <code className="bg-gray-100 px-1 rounded">{`{{{field}}}`}</code> for HTML fields like description, solution, etc.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Text Body (Plain Text) <span className="text-red-500">*</span>
                      </label>
                      <div className="border border-gray-300 rounded-md overflow-hidden">
                        <CodeMirror
                          value={formData.textBody}
                          height="350px"
                          onChange={(value) => setFormData({ ...formData, textBody: value })}
                          theme="light"
                          placeholder="Plain text version of your email template..."
                          basicSetup={{
                            lineNumbers: true,
                            highlightActiveLineGutter: true,
                            highlightSpecialChars: true,
                            foldGutter: true,
                            drawSelection: true,
                            dropCursor: true,
                            allowMultipleSelections: true,
                            indentOnInput: true,
                            bracketMatching: true,
                            closeBrackets: true,
                            autocompletion: true,
                            rectangularSelection: true,
                            crosshairCursor: true,
                            highlightActiveLine: true,
                            highlightSelectionMatches: true,
                            closeBracketsKeymap: true,
                            searchKeymap: true,
                            foldKeymap: true,
                            completionKeymap: true,
                            lintKeymap: true,
                          }}
                        />
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        Plain text version for email clients that don't support HTML
                      </p>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-900">
                        Active (template will be available for notification rules)
                      </label>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setShowFormModal(false)}
                      disabled={saving}
                      className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSave}
                      disabled={saving}
                      className="inline-flex justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:bg-gray-300"
                    >
                      {saving ? 'Saving...' : editingTemplate ? 'Update Template' : 'Create Template'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Preview Modal */}
      <Transition appear show={showPreviewModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowPreviewModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-5xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex items-center justify-between mb-4">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      Template Preview: {previewTemplate?.name}
                    </Dialog.Title>
                    <button
                      onClick={() => setShowPreviewModal(false)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="mt-4">
                    {previewLoading ? (
                      <div className="flex justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                      </div>
                    ) : (
                      <div className="border border-gray-300 rounded-lg p-4 bg-gray-50 max-h-[70vh] overflow-y-auto">
                        <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(previewHtml) }} />
                      </div>
                    )}
                  </div>

                  <div className="mt-6 flex justify-end">
                    <button
                      type="button"
                      onClick={() => setShowPreviewModal(false)}
                      className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Close
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Delete Confirmation Modal */}
      <Transition appear show={showDeleteModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowDeleteModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                    Delete Email Template
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Are you sure you want to delete the template <strong>{deletingTemplate?.name}</strong>?
                      This action cannot be undone.
                    </p>
                  </div>

                  <div className="mt-4 flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowDeleteModal(false);
                        setDeletingTemplate(null);
                      }}
                      className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleDelete}
                      className="inline-flex justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
                    >
                      Delete
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default EmailTemplatesManagement;
